// Placeholder/restore for proper nouns — the pure half of the glossary.
// Ported from qt2's shipped mechanism (2026-07-27): protect exact Chinese
// terms with model-tested markers before decoding so the model copies an
// opaque token instead of transliterating a name, then restore the target
// text after detok. Strict restore only: a row whose markers come back
// wrong is the CALLER's problem (it retries from raw source) — fuzzy
// repair miscorrects, which qt2 found worse than a miss.
//
// Rows arriving here are already t2s-converted (translateText converts
// before segmentation), so compile converts term zh the same way and the
// two meet in simplified space.
import { t2s } from './t2s.js';

// Six markers that survived qt2's beam-4 CT2 eval (127/127 for QX7-class
// tokens vs 4/127 for bracket forms). Order matters: assignment walks this
// list. Re-verified on this engine's greedy decode by the marker-survival
// eval before ship.
export const MARKER_POOL = ['QX7', 'KX7', 'RX7', 'ZX9', 'ZQ1', 'VX1'];

// FNV-1a 32-bit — stable, sync, dependency-free. Only used to keep TM keys
// short; collisions are harmless (a stale hit would still be a real
// translation of that exact raw row under SOME term set, and the row
// re-decodes on the next glossary edit anyway).
function fnv1a(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

// Shared framing for row-level glossary signatures. With no `extra` material
// this is byte-identical to F1's historical hash; auto-dict rows append a
// tagged artifact/protocol frame while user-only rows keep their old TM key.
export function signatureFromPairs(pairs, extra = '') {
  const body = [...new Set(pairs)].sort().join('\u001e');
  return fnv1a(extra ? `${body}\u001d${extra}` : body);
}

export function compileGlossary(terms) {
  const active = (terms ?? [])
    .filter((t) => t?.enabled !== false && t?.zh && t?.vi)
    .map((t) => ({ zh: t2s(t.zh).text, vi: t.vi }))
    .filter((t, i, a) => a.findIndex((x) => x.zh === t.zh) === i)
    .sort((a, b) => b.zh.length - a.zh.length); // longest-first matching
  return active.length ? { terms: active } : null;
}

// All term occurrences in a row as non-overlapping spans. Longest terms
// claim first, so a short term can never match inside a longer one's span;
// a standalone occurrence of the short term elsewhere still counts.
function matchSpans(row, compiled) {
  const spans = [];
  for (const t of compiled.terms) {
    let at = 0;
    while ((at = row.indexOf(t.zh, at)) !== -1) {
      const end = at + t.zh.length;
      if (!spans.some((s) => at < s.end && end > s.start)) {
        spans.push({ start: at, end, term: t });
      }
      at = end;
    }
  }
  return spans.sort((a, b) => a.start - b.start);
}

export function protectRow(row, compiled) {
  if (!compiled) return null;
  const spans = matchSpans(row, compiled);
  if (!spans.length) return null;
  // A marker string already present in the raw text may not be used —
  // restore counts occurrences and could not tell ours from the text's.
  const pool = MARKER_POOL.filter((m) => !row.includes(m));
  const byTerm = new Map(); // zh → mark; insertion order = first occurrence
  const used = [];
  for (const s of spans) {
    let mark = byTerm.get(s.term.zh);
    if (!mark) {
      if (!pool.length) continue; // beyond six distinct terms: stays raw
      mark = { marker: pool.shift(), vi: s.term.vi, count: 0 };
      byTerm.set(s.term.zh, mark);
    }
    mark.count++;
    used.push({ start: s.start, end: s.end, marker: mark.marker });
  }
  if (!used.length) return null;
  // Replace right-to-left so earlier offsets stay valid. Span replacement,
  // not string replacement: a nested occurrence of another term must not
  // be eaten (小胖 inside 安小胖).
  let text = row;
  for (let i = used.length - 1; i >= 0; i--) {
    const u = used[i];
    text = text.slice(0, u.start) + u.marker + text.slice(u.end);
  }
  return { text, marks: [...byTerm.values()] };
}

// Strict: every marker must come back exactly as many times as it went in.
// The text is best-effort restored either way — for a row that will not be
// retried (cancelled run), a readable name beats a bare marker.
export function restoreRow(out, marks) {
  let ok = true;
  let text = out;
  for (const m of marks) {
    const n = text.split(m.marker).length - 1;
    if (n !== m.count) ok = false;
    if (n) text = text.split(m.marker).join(m.vi);
  }
  return { text, ok };
}

// F1: the part of a TM key that says "translated under these terms".
// Covers every matched term (including any beyond the six-marker cap —
// over-invalidation just re-decodes, serving stale is the real hazard);
// rows with no match return null and keep their raw key byte-identical.
export function rowSignature(row, compiled) {
  if (!compiled) return null;
  const spans = matchSpans(row, compiled);
  if (!spans.length) return null;
  return signatureFromPairs(spans.map((s) => `${s.term.zh}\u001f${s.term.vi}`));
}
